import { useImperativeHandle, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import {
  SpaceViewer,
  type SpaceAxesOrientation,
  type SpaceSceneNode,
  type SpaceTool,
} from "./SpaceViewer.jsx";

/**
 * MetaRoom-style shell for the textured Space model.
 *
 * The viewer alone is a canvas; the workspace around it is what makes it
 * usable: a layer tree on the left (with what the model is made of — how many
 * photographs, whether its scale is metric), a floating pill toolbar for the
 * actions that operate on the whole scene, and a live XYZ gizmo in the corner
 * that turns with the camera so the reader always knows which way is up.
 *
 * The measure tool is only offered on a metric model. A model built from
 * photographs alone has an arbitrary scale — measured 2026-09-10, a room
 * 2.5 m high came back 1.16 m high without LiDAR depth — so a tape measure on
 * it would print confident wrong numbers. With depth it came back 2.53 m.
 */

export interface SpaceModelInfo {
  readonly imageCount?: number | null;
  readonly metricScale?: boolean | null;
  readonly secondsProcessing?: number | null;
  readonly finishedAt?: string | null;
}

export interface SpaceWorkspaceProps {
  /** Identifies the model (its URL); a change reloads it. */
  readonly modelUrl: string;
  /** Fetches the GLB bytes through the authenticated client. */
  readonly loadModel: () => Promise<ArrayBuffer>;
  /** Saves the USDZ (or whatever the download is); absent hides the button. */
  readonly onDownload?: () => Promise<void> | void;
  readonly info?: SpaceModelInfo;
}

export function SpaceWorkspace({
  modelUrl,
  loadModel,
  onDownload,
  info,
}: SpaceWorkspaceProps): ReactNode {
  const metric = info?.metricScale === true;
  const [nodes, setNodes] = useState<readonly SpaceSceneNode[]>([]);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [hiddenUuids, setHiddenUuids] = useState<ReadonlySet<string>>(() => new Set());
  const [activeTool, setActiveTool] = useState<SpaceTool>("select");
  const [query, setQuery] = useState("");
  const [background, setBackground] = useState("#0d141c");
  const [wireframe, setWireframe] = useState(false);
  const [showAxes, setShowAxes] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [fitSignal, setFitSignal] = useState(0);
  const [resetSignal, setResetSignal] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const gizmo = useRef<AxesGizmoHandle>(null);

  function toggleHidden(uuid: string): void {
    setHiddenUuids((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  const filteredNodes = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? nodes.filter((n) => n.name.toLowerCase().includes(needle)) : nodes;
  }, [nodes, query]);

  const triangles = nodes.reduce((sum, n) => sum + n.triangles, 0);

  function flashToast(message: string): void {
    setToast(message);
    window.setTimeout(() => setToast(null), 2200);
  }

  async function onShare(): Promise<void> {
    try {
      await navigator.clipboard.writeText(window.location.href);
      flashToast("Link copied");
    } catch {
      flashToast("Could not copy — the browser blocked clipboard access.");
    }
  }

  async function download(): Promise<void> {
    if (!onDownload) return;
    try {
      await onDownload();
    } catch {
      flashToast("The model could not be downloaded. Try again.");
    }
  }

  return (
    <div className="space-workspace" aria-label="3D space workspace">
      <aside className="space-workspace__sidebar" aria-label="Layer tree">
        <div className="space-workspace__sidebar-header">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search layers"
            aria-label="Search layers"
            className="space-workspace__search"
          />
        </div>
        <SpaceLayerTree
          nodes={filteredNodes}
          selectedUuid={selectedUuid}
          hiddenUuids={hiddenUuids}
          onSelect={setSelectedUuid}
          onToggleHidden={toggleHidden}
        />
        <dl className="space-workspace__facts" aria-label="About this model">
          {info?.imageCount ? (
            <>
              <dt>Photographs</dt>
              <dd>{info.imageCount}</dd>
            </>
          ) : null}
          {triangles > 0 ? (
            <>
              <dt>Triangles</dt>
              <dd>{triangles.toLocaleString("en-GB")}</dd>
            </>
          ) : null}
          <dt>Scale</dt>
          <dd>
            {metric ? (
              "Metric (LiDAR depth)"
            ) : (
              <span title="Built from photographs without LiDAR depth, so sizes are relative, not metres.">
                Not to scale
              </span>
            )}
          </dd>
          {info?.secondsProcessing ? (
            <>
              <dt>Built in</dt>
              <dd>{formatDuration(info.secondsProcessing)}</dd>
            </>
          ) : null}
        </dl>
      </aside>

      <div className="space-workspace__stage">
        <div className="space-workspace__toolbar-row">
          <div className="space-workspace__toolbar" role="toolbar" aria-label="Space viewer tools">
            <ToolButton label="Orbit" hint="Drag to turn the model" active={activeTool === "select"} onClick={() => setActiveTool("select")}>
              <OrbitIcon />
            </ToolButton>
            <ToolButton label="Pan" hint="Drag to slide the model (or right-drag in any tool)" active={activeTool === "pan"} onClick={() => setActiveTool(activeTool === "pan" ? "select" : "pan")}>
              <PanIcon />
            </ToolButton>
            <ToolButton
              label="Measure"
              hint={metric ? "Click two points to measure between them" : "Needs a LiDAR capture — this model is not to scale"}
              active={activeTool === "measure"}
              disabled={!metric}
              onClick={() => setActiveTool(activeTool === "measure" ? "select" : "measure")}
            >
              <RulerIcon />
            </ToolButton>
            <span className="space-workspace__toolbar-divider" aria-hidden="true" />
            <ToolButton label="Fit to view" hint="Frame the whole model" onClick={() => setFitSignal((n) => n + 1)}>
              <FitIcon />
            </ToolButton>
            <ToolButton label="Reset view" hint="Back to the starting angle" onClick={() => setResetSignal((n) => n + 1)}>
              <ResetIcon />
            </ToolButton>
            <ToolButton label="Coordinate axes" hint="Show or hide the X/Y/Z axes" active={showAxes} onClick={() => setShowAxes((v) => !v)}>
              <AxesIcon />
            </ToolButton>
            <span className="space-workspace__toolbar-divider" aria-hidden="true" />
            <ToolButton label="Share (copy link)" hint="Copy a link to this survey" onClick={onShare}>
              <ShareIcon />
            </ToolButton>
            {onDownload ? (
              <ToolButton label="Download model" hint="Download the USDZ (opens in Quick Look on iPhone and Mac)" onClick={download}>
                <DownloadIcon />
              </ToolButton>
            ) : null}
          </div>
          <button
            type="button"
            className="space-workspace__toolbar-gear"
            aria-label="Viewer settings"
            aria-pressed={settingsOpen}
            onClick={() => setSettingsOpen((v) => !v)}
            title="Background colour and wireframe"
          >
            <GearIcon />
          </button>
        </div>
        {settingsOpen ? (
          <div className="space-workspace__settings" role="dialog" aria-label="Viewer settings">
            <div className="space-workspace__settings-head">
              <strong>Viewer</strong>
              <button type="button" className="space-workspace__settings-close" aria-label="Close settings" onClick={() => setSettingsOpen(false)}>
                ×
              </button>
            </div>
            <label className="space-workspace__settings-field">
              <span>Background</span>
              <input type="color" value={background} onChange={(event) => setBackground(event.target.value)} aria-label="Canvas background colour" />
            </label>
            <label className="space-workspace__settings-field">
              <input type="checkbox" checked={wireframe} onChange={(event) => setWireframe(event.target.checked)} />
              <span>Wireframe overlay</span>
            </label>
          </div>
        ) : null}
        <div className="space-workspace__canvas">
          <SpaceViewer
            modelKey={modelUrl}
            loadModel={loadModel}
            hiddenUuids={hiddenUuids}
            selectedUuid={selectedUuid}
            onSelectMesh={setSelectedUuid}
            onNodesChange={setNodes}
            onOrientation={(axes) => gizmo.current?.update(axes)}
            activeTool={activeTool}
            backgroundColor={background}
            wireframe={wireframe}
            showAxes={showAxes}
            metric={metric}
            fitSignal={fitSignal}
            resetSignal={resetSignal}
          />
        </div>
        <p className="space-workspace__hint" aria-hidden="true">
          Drag to orbit · right-drag to pan · scroll to zoom
        </p>
        <AxesGizmo ref={gizmo} />
        {toast ? (
          <div className="space-workspace__toast" role="status" aria-live="polite">
            {toast}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return rest ? `${m} min ${rest} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

function SpaceLayerTree({
  nodes,
  selectedUuid,
  hiddenUuids,
  onSelect,
  onToggleHidden,
}: {
  readonly nodes: readonly SpaceSceneNode[];
  readonly selectedUuid: string | null;
  readonly hiddenUuids: ReadonlySet<string>;
  readonly onSelect: (uuid: string | null) => void;
  readonly onToggleHidden: (uuid: string) => void;
}): ReactNode {
  return (
    <ul className="space-workspace__tree" role="tree">
      <li role="treeitem" aria-expanded="true" aria-selected={false}>
        <div className="space-workspace__tree-row space-workspace__tree-row--root">
          <span className="space-workspace__tree-caret" aria-hidden="true">▾</span>
          <span>Building</span>
        </div>
        <ul role="group">
          <li role="treeitem" aria-expanded="true" aria-selected={false}>
            <div className="space-workspace__tree-row space-workspace__tree-row--branch">
              <span className="space-workspace__tree-caret" aria-hidden="true">▾</span>
              <span>Floor 0</span>
            </div>
            <ul role="group">
              {nodes.length === 0 ? (
                <li className="space-workspace__tree-empty">
                  <span className="muted small">Layers appear once the model is loaded.</span>
                </li>
              ) : null}
              {nodes.map((node) => {
                const hidden = hiddenUuids.has(node.uuid);
                const selected = node.uuid === selectedUuid;
                return (
                  <li key={node.uuid} role="treeitem" aria-selected={selected}>
                    <div className={"space-workspace__tree-row space-workspace__tree-row--leaf" + (selected ? " is-selected" : "")}>
                      <button type="button" className="space-workspace__tree-label" onClick={() => onSelect(selected ? null : node.uuid)} aria-pressed={selected}>
                        {node.name}
                      </button>
                      <button
                        type="button"
                        className="space-workspace__tree-eye"
                        aria-label={hidden ? `Show ${node.name}` : `Hide ${node.name}`}
                        aria-pressed={hidden}
                        title={hidden ? "Show this layer" : "Hide this layer"}
                        onClick={() => onToggleHidden(node.uuid)}
                      >
                        {hidden ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        </ul>
      </li>
    </ul>
  );
}

function ToolButton({
  label,
  hint,
  active,
  disabled,
  onClick,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly onClick?: () => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      className={"space-workspace__tool" + (active ? " is-active" : "")}
      aria-label={label}
      aria-pressed={active ?? false}
      title={hint ? `${label} — ${hint}` : label}
      disabled={disabled ?? false}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

interface AxesGizmoHandle {
  update(axes: SpaceAxesOrientation): void;
}

/**
 * The XYZ gizmo, bottom-right. It turns with the camera: the viewer reports
 * where each world axis points on screen and this moves the three lines
 * directly (no React render per frame of a drag). Axes pointing away from the
 * viewer are drawn fainter, so the gizmo reads in depth. Until a model loads —
 * or in a browser without WebGL — it shows the default isometric pose.
 */
function AxesGizmo({ ref }: { readonly ref: Ref<AxesGizmoHandle> }): ReactNode {
  const lines = useRef<Record<"x" | "y" | "z", SVGGElement | null>>({ x: null, y: null, z: null });
  useImperativeHandle(ref, () => ({
    update(axes: SpaceAxesOrientation) {
      for (const key of ["x", "y", "z"] as const) {
        const group = lines.current[key];
        if (!group) continue;
        const [dx, dy, dz] = axes[key];
        const x2 = 32 + dx * 22;
        const y2 = 32 + dy * 22;
        const line = group.querySelector("line");
        const tip = group.querySelector("circle");
        const text = group.querySelector("text");
        line?.setAttribute("x2", x2.toFixed(1));
        line?.setAttribute("y2", y2.toFixed(1));
        tip?.setAttribute("cx", x2.toFixed(1));
        tip?.setAttribute("cy", y2.toFixed(1));
        text?.setAttribute("x", (32 + dx * 29).toFixed(1));
        text?.setAttribute("y", (32 + dy * 29 + 3).toFixed(1));
        group.setAttribute("opacity", dz > 0.2 ? "0.45" : "1");
      }
    },
  }), []);
  const axis = (key: "x" | "y" | "z", colour: string, x2: number, y2: number) => (
    <g ref={(el) => { lines.current[key] = el; }} data-axis={key}>
      <line x1="32" y1="32" x2={x2} y2={y2} stroke={colour} strokeWidth="2.2" strokeLinecap="round" />
      <circle cx={x2} cy={y2} r="3" fill={colour} />
      <text x={32 + (x2 - 32) * 1.32} y={32 + (y2 - 32) * 1.32 + 3} fontSize="9" fontWeight="700" textAnchor="middle" fill={colour}>
        {key.toUpperCase()}
      </text>
    </g>
  );
  return (
    <svg className="space-workspace__axes" data-testid="space-axes" viewBox="0 0 64 64" role="img" aria-label="XYZ axes">
      <title>XYZ axes</title>
      {axis("z", "#64b6ff", 17, 44)}
      {axis("x", "#ff6b5e", 51, 43)}
      {axis("y", "#5fe0a0", 32, 10)}
    </svg>
  );
}

// ----------------------------- Icons -----------------------------
// Inline SVGs, 16x16 on a currentColor stroke, so a pressed button colours
// them through CSS alone.

const iconProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function OrbitIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <ellipse cx="12" cy="12" rx="9" ry="4" />
      <path d="M12 3 a4 9 0 0 1 0 18" />
      <path d="M18 5 l2 1 -1 2" />
    </svg>
  );
}
function PanIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <path d="M12 3 v18 M3 12 h18" />
      <path d="M9 6 l3 -3 3 3 M9 18 l3 3 3 -3 M6 9 l-3 3 3 3 M18 9 l3 3 -3 3" />
    </svg>
  );
}
function RulerIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <rect x="2" y="9" width="20" height="6" transform="rotate(-30 12 12)" />
      <path d="M6 10 v2 M9 8.5 v3 M12 7 v4 M15 5.5 v3 M18 4 v3" />
    </svg>
  );
}
function FitIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <path d="M4 9 V4 h5 M20 9 V4 h-5 M4 15 v5 h5 M20 15 v5 h-5" />
      <rect x="8" y="8" width="8" height="8" rx="1" />
    </svg>
  );
}
function ResetIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <path d="M4 12 a8 8 0 1 0 2.3 -5.6" />
      <path d="M4 4 v4 h4" />
    </svg>
  );
}
function AxesIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <path d="M6 18 V4 M6 18 h14 M6 18 l-4 4" />
      <path d="M4 6 l2 -2 2 2 M18 16 l2 2 -2 2" />
    </svg>
  );
}
function ShareIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8 11 L16 7 M8 13 L16 17" />
    </svg>
  );
}
function DownloadIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <path d="M12 3 v12 M6 11 l6 6 6-6" />
      <path d="M4 20 h16" />
    </svg>
  );
}
function GearIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2 v3 M12 19 v3 M2 12 h3 M19 12 h3 M4.9 4.9 l2.1 2.1 M17 17 l2.1 2.1 M4.9 19.1 l2.1 -2.1 M17 7 l2.1 -2.1" />
    </svg>
  );
}
function EyeIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <path d="M1 12 s4 -7 11 -7 s11 7 11 7 s-4 7 -11 7 s-11 -7 -11 -7 z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOffIcon(): ReactNode {
  return (
    <svg {...iconProps}>
      <path d="M3 3 l18 18" />
      <path d="M10.6 6.1 A11 11 0 0 1 12 6 c7 0 11 6 11 6 a13 13 0 0 1 -3.2 3.6" />
      <path d="M6.2 8 A13 13 0 0 0 1 12 s4 6 11 6 a10 10 0 0 0 3.8 -0.7" />
    </svg>
  );
}
